import React, { useContext, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { User } from '../types/ViewModel/User';
import { Role } from '../types/ViewModel/Role';
import { RoleTypeEnum } from '../enums/roleTypeEnum';
import { PbiAuthorization, PbiResponse } from '../types/PbiTypes';

type AuthContextType = {
    loggedInUser: User | null;
    setLoggedInUser: (user: User | null) => void;
    userIsUpdated: boolean;
    setUserIsUpdated: (isUpdated: boolean) => void;
    rolesAreUpdated: boolean;
    setRolesAreUpdated: (isUpdated: boolean) => void;
    isAuthenticating: boolean;
    setIsAuthenticating: (isAuthenticating: boolean) => void;
    isVerifyingToken: boolean;
    setIsVerifyingToken: (isVerifyingToken: boolean) => void;
    token: string;
    setToken: (token: string) => void;
    loggedInRole: Role;
    setLoggedInRole: (role: Role) => void;
    role: Role;
    setRole: (role: Role) => void;
    signalRConnectionId: string | null;
    setSignalRConnectionId: (connId: string | null) => void;
    pbiAccessToken: PbiResponse | null;
    setPbiAccessToken: (pbiAccessToken: PbiResponse | null) => void;
    pbiAuthorization: PbiAuthorization | null;
    setPbiAuthorization: (pbiAuthorization: PbiAuthorization | null) => void;
};

export const AuthContext = React.createContext<AuthContextType>(undefined!);

export const AuthContextProvider = ({ children }: ContextProps) => {
    const [loggedInUser, setLoggedInUser] = useState<User | null>(null);
    const [userIsUpdated, setUserIsUpdated] = useState<boolean>(false);
    const [rolesAreUpdated, setRolesAreUpdated] = useState<boolean>(false);
    const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
    const [isVerifyingToken, setIsVerifyingToken] = useState<boolean>(true);
    const [token, setToken] = useState<string>('');
    const [loggedInRole, setLoggedInRole] = useState<Role>(new Role(RoleTypeEnum.Unknown, false, false));
    const [role, setRole] = useState<Role>(new Role(RoleTypeEnum.Unknown, false, false));
    const [signalRConnectionId, setSignalRConnectionId] = useState<string | null>(null);
    const [pbiAccessToken, setPbiAccessToken] = useState<PbiResponse | null>(null);
    const [pbiAuthorization, setPbiAuthorization] = useState<PbiAuthorization | null>(null);

    return (
        <AuthContext.Provider
            value={{
                loggedInUser,
                setLoggedInUser,
                userIsUpdated,
                setUserIsUpdated,
                rolesAreUpdated,
                setRolesAreUpdated,
                isAuthenticating,
                setIsAuthenticating,
                isVerifyingToken,
                setIsVerifyingToken,
                token,
                setToken,
                loggedInRole,
                setLoggedInRole,
                role,
                setRole,
                signalRConnectionId,
                setSignalRConnectionId,
                pbiAccessToken,
                setPbiAccessToken,
                pbiAuthorization,
                setPbiAuthorization,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthStore = () => useContext(AuthContext);
